import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaShieldAlt, FaInfoCircle, FaEnvelope } from 'react-icons/fa';
import Loader from '../components/Loader';
import { buildApiUrl } from '../utils/api';

const POLICY_TYPES = {
  privacy: {
    label: 'Privacy Policy',
    description: 'How customer information, orders, payments, and account data are handled.',
    emptyMessage: 'No privacy policy content is available right now.',
    loadingLabel: 'Loading privacy policy',
  },
  terms: {
    label: 'Terms & Conditions',
    description: 'The terms that apply when you use Kisan Daily Fresh services.',
    emptyMessage: 'No terms and conditions content is available right now.',
    loadingLabel: 'Loading terms and conditions',
  },
  refund: {
    label: 'Refund Policy',
    description: 'Our policy for returns, cancellations, and refunds.',
    emptyMessage: 'No refund policy content is available right now.',
    loadingLabel: 'Loading refund policy',
  },
};

const formatDate = (value) => {
  if (!value) return null;
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const PrivacyPolicy = () => {
  const { policyType } = useParams();
  const type = POLICY_TYPES[policyType] ? policyType : 'privacy';
  const policyDetails = POLICY_TYPES[type];
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;

    const fetchPolicy = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          buildApiUrl(`/api/v1/customer/privacy-policy?type=${type}`),
          { headers: { 'Content-Type': 'application/json' } }
        );
        const json = await response.json();
        if (!response.ok || !json.success) {
          throw new Error(json.message || `Failed to load ${policyDetails.label.toLowerCase()}`);
        }

        const policyData = Array.isArray(json.data) ? json.data[0] : json.data;
        if (alive) setPolicy(policyData || null);
      } catch (err) {
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchPolicy();
    return () => { alive = false; };
  }, [type, policyDetails.label]);

  if (loading) {
    return <Loader fullScreen label={policyDetails.loadingLabel} sublabel="Fetching the latest customer policy." />;
  }

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-3xl bg-white shadow-xl shadow-black/5">
          <div className="bg-primary-700 p-8 text-white">
            <div className="mb-3 flex items-center gap-3">
              <FaShieldAlt className="text-2xl" />
              <h1 className="text-3xl font-black tracking-tight">
                {policy?.title || policyDetails.label}
              </h1>
            </div>
            <p className="max-w-2xl text-base leading-relaxed text-primary-50">
              {policyDetails.description}
            </p>
          </div>

          <div className="p-6 sm:p-8">
            {error ? (
              <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-center">
                <p className="font-bold text-red-600">Failed to load {policyDetails.label.toLowerCase()}</p>
                <p className="mt-1 text-sm text-red-500">{error}</p>
              </div>
            ) : (
              <>
                <div className="mb-8 rounded-2xl border border-primary-100 bg-primary-50 p-5">
                  <div className="flex items-start gap-4">
                    <div className="rounded-xl bg-white p-3 text-primary-700 shadow-sm">
                      <FaInfoCircle size={22} />
                    </div>
                    <div>
                      <h2 className="text-lg font-black text-gray-900">Last Updated</h2>
                      <p className="mt-1 text-sm leading-relaxed text-gray-600">
                        {formatDate(policy?.updated_at) || 'Latest policy from Kisan Daily Fresh'}
                      </p>
                    </div>
                  </div>
                </div>

                <article className="prose prose-sm max-w-none text-gray-700 sm:prose-base prose-headings:text-gray-900 prose-strong:text-gray-900">
                  {policy?.content ? (
                    /<\/?[a-z][\s\S]*>/i.test(policy.content) ? (
                      <div dangerouslySetInnerHTML={{ __html: policy.content }} />
                    ) : (
                      <p>{policy.content}</p>
                    )
                  ) : (
                    <p>{policyDetails.emptyMessage}</p>
                  )}
                </article>

                <div className="mt-10 rounded-2xl border border-gray-100 bg-gray-50 p-5">
                  <div className="flex items-center gap-3">
                    <div className="rounded-xl bg-white p-3 text-primary-700 shadow-sm">
                      <FaEnvelope size={20} />
                    </div>
                    <div>
                      <h3 className="font-black text-gray-900">Contact Us</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        For questions about this policy, contact{' '}
                        <a href="mailto:info@kisandailyfresh.in" className="font-bold text-primary-700 hover:underline">
                          info@kisandailyfresh.in
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
